import React, { useCallback, useMemo } from 'react';

import { useMutation } from '@betterment/js-runtime';
import { ClassicCard } from '@braze/web-sdk';

import { urlBuilder } from '../../Shared/urlBuilder';
import { LocationMessageCounts } from '../constants';
import {
  ContentCardLocation,
  FilterAndLogMessagesByLocationParams,
  MarketingMessage,
  MessageByLocation,
} from '../data';
import { useBrazeContentCard } from '../hooks/useBrazeContentCard';

type ContentCardsParam = ClassicCard & { created?: number; expiresAt?: number };

export const MessageContext = React.createContext<MessageByLocation>({
  filterAndLogMessageByLocation: () => Promise.resolve(),
  isLoading: false,
  isReadyToFilter: false,
  refresh: () => Promise.resolve(),
});

export const MessageProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [postMessageByLocation, { loading: isPostMessageByLocationLoading, data: postMessageByLocationData }] =
    useMutation<
      { message: MarketingMessage },
      { location: ContentCardLocation; goalId?: string; contentCards: ContentCardsParam[] }
    >(urlBuilder.apiV2MarketingMessages(), {
      method: 'POST',
    });

  const [postMessagesByLocation, { loading: isPostMessagesByLocationLoading, data: postMessagesByLocationData }] =
    useMutation<
      { messages: MarketingMessage[] },
      { location: ContentCardLocation; goalId?: string; contentCards: ContentCardsParam[]; messageCount: number }
    >(urlBuilder.apiV2MarketingMessagesGroups(), {
      method: 'POST',
    });

  const { cards, refresh, isRefreshing } = useBrazeContentCard();
  const isReadyToFilter = cards.length > 0 && !isRefreshing;

  const filterAndLogMessageByLocation = useCallback(
    async ({ location, goalId, isMultipleMessages }: FilterAndLogMessagesByLocationParams) => {
      if (isReadyToFilter) {
        const formattedCards = cards.map(
          card =>
            ({
              ...card,
              created: card.created?.getTime(),
              expiresAt: card.expiresAt?.getTime(),
            }) as ContentCardsParam
        );

        if (isMultipleMessages) {
          await postMessagesByLocation({
            location,
            goalId: goalId?.toString(),
            contentCards: formattedCards,
            messageCount: LocationMessageCounts[location],
          });

          return;
        }

        await postMessageByLocation({
          location,
          goalId: goalId?.toString(),
          contentCards: formattedCards,
        });
      }
    },
    [cards, postMessageByLocation, postMessagesByLocation, isReadyToFilter]
  );

  const selectedContentCard = cards.find(card => card.id === postMessageByLocationData?.message?.id);

  const messages = postMessagesByLocationData?.messages?.map(message => {
    return {
      messageContent: message,
      brazeCard: cards.find(card => card.id === message.id),
    };
  });

  const values = useMemo(
    () => ({
      filterAndLogMessageByLocation,
      message: postMessageByLocationData?.message,
      messages,
      contentCard: selectedContentCard,
      isLoading: isPostMessageByLocationLoading || isPostMessagesByLocationLoading || isRefreshing,
      isReadyToFilter,
      refresh,
    }),
    [
      filterAndLogMessageByLocation,
      postMessageByLocationData,
      messages,
      isPostMessageByLocationLoading,
      isPostMessagesByLocationLoading,
      isReadyToFilter,
      refresh,
      selectedContentCard,
      isRefreshing,
    ]
  );

  return <MessageContext.Provider value={values}>{children}</MessageContext.Provider>;
};

export const useMessageByLocation = () => {
  const context = React.useContext(MessageContext);
  if (context.filterAndLogMessageByLocation === undefined) {
    throw new Error('useMessageByLocation must be used within a MessageProvider');
  }
  return context;
};
